import api, {
    apiDelete,
    apiGet,
    apiPost,
    apiPostForm,
    apiPut,
    apiPutForm,
    apiUrl
} from './apiClient'
import env from '../configs/env'
import type {
    ActivityLogEntry,
    AppNotification,
    AppUser,
    DashboardStats,
    JournalStatus,
    Payment,
    Review,
    ReviewDecision,
    Submission,
    UserRole
} from '@shared/interfaces/appInterface'


type Success<T> = T & { success: boolean }

interface ListSubmissions {
    success: boolean
    submissions: Submission[]
}
interface OneSubmission {
    success: boolean
    submission: Submission
}
interface ListReviews {
    success: boolean
    reviews: Review[]
}
interface ListPayments {
    success: boolean
    payments: Payment[]
}
interface ListUsers {
    success: boolean
    users: AppUser[]
}
interface OneUser {
    success: boolean
    user: AppUser
}
interface ListNotifications {
    success: boolean
    notifications: AppNotification[]
}
interface StatsResponse {
    success: boolean
    stats: DashboardStats
}


export const authorService = {
    listMine: async (): Promise<Submission[]> => {
        const r = await apiGet<ListSubmissions>('/submissions/my')
        return r.submissions ?? []
    },
    getOne: async (id: string): Promise<Submission> => {
        const r = await apiGet<OneSubmission>(`/submissions/${id}`)
        return r.submission
    },
    submit: async (input: {
        title: string
        abstract: string
        keywords?: string
        domain?: string
        coverLetter?: string
        coAuthors?: { name: string; email?: string; affiliation?: string }[]
        file: File
    }): Promise<Submission> => {
        const fd = new FormData()
        fd.append('title', input.title)
        fd.append('abstract', input.abstract)
        if (input.keywords) fd.append('keywords', input.keywords)
        if (input.domain) fd.append('domain', input.domain)
        if (input.coverLetter) fd.append('coverLetter', input.coverLetter)
        if (input.coAuthors?.length) fd.append('coAuthors', JSON.stringify(input.coAuthors))
        fd.append('file', input.file)
        const r = await apiPostForm<OneSubmission>('/submissions', fd)
        return r.submission
    },
    revise: async (id: string, file: File): Promise<Submission> => {
        const fd = new FormData()
        fd.append('file', file)
        const r = await apiPostForm<OneSubmission>(`/submissions/${id}/revision`, fd)
        return r.submission
    },
    listMyPayments: async (): Promise<Payment[]> => {
        const r = await apiGet<ListPayments>('/payments/my')
        return r.payments ?? []
    },
    createPayment: async (input: {
        submissionId: string
        amount: number
        method: string
        transactionId: string
        notes?: string
    }): Promise<Payment> => {
        const r = await apiPost<Success<{ payment: Payment }>>('/payments', input)
        return r.payment
    }
}


export const reviewerService = {
    listAssignments: async (): Promise<Review[]> => {
        const r = await apiGet<ListReviews>('/reviews/my')
        return r.reviews ?? []
    },
    submitDecision: async (
        reviewId: string,
        input: { decision: ReviewDecision; comments: string; recommendation?: string; file?: File }
    ): Promise<Review> => {
        const fd = new FormData()
        fd.append('decision', input.decision)
        fd.append('comments', input.comments)
        if (input.recommendation) fd.append('recommendation', input.recommendation)
        if (input.file) fd.append('file', input.file)
        const r = await apiPutForm<Success<{ review: Review }>>(`/reviews/${reviewId}/submit`, fd)
        return r.review
    },
    toggleAvailability: async (available: boolean): Promise<AppUser> => {
        const r = await apiPut<OneUser>('/reviews/availability', { available })
        return r.user
    }
}


export const adminService = {
    listSubmissions: async (): Promise<Submission[]> => {
        const r = await apiGet<ListSubmissions>('/submissions')
        return r.submissions ?? []
    },
    getSubmission: async (id: string): Promise<Submission> => {
        const r = await apiGet<OneSubmission>(`/submissions/${id}`)
        return r.submission
    },
    updateStatus: async (id: string, status: JournalStatus, note?: string): Promise<Submission> => {
        const r = await apiPut<OneSubmission>(`/submissions/${id}/status`, { status, note })
        return r.submission
    },
    assignReviewer: async (id: string, reviewerId: string): Promise<Submission> => {
        const r = await apiPost<OneSubmission>(`/submissions/${id}/assign`, { reviewerId })
        return r.submission
    },
    deleteSubmission: (id: string) => apiDelete<{ success: boolean }>(`/submissions/${id}`),
    forwardComment: async (
        submissionId: string,
        input: { note?: string }
    ): Promise<Submission> => {
        const r = await apiPost<OneSubmission>(`/reviews/forward/${submissionId}`, input)
        return r.submission
    },

    listUsers: async (role?: UserRole): Promise<AppUser[]> => {
        const url = role ? `/users?role=${role}` : '/users'
        const r = await apiGet<ListUsers>(url)
        return r.users ?? []
    },
    getUser: async (id: string): Promise<AppUser> => {
        const r = await apiGet<OneUser>(`/users/${id}`)
        return r.user
    },
    createUser: async (input: {
        name: string
        email: string
        password: string
        role: UserRole
        phone?: string
        institution?: string
        department?: string
        specialization?: string
    }): Promise<AppUser> => {
        const r = await apiPost<OneUser>('/users', input)
        return r.user
    },
    updateUser: async (id: string, patch: Partial<AppUser>): Promise<AppUser> => {
        const r = await apiPut<OneUser>(`/users/${id}`, patch)
        return r.user
    },
    deleteUser: (id: string) => apiDelete<{ success: boolean }>(`/users/${id}`),
    toggleUserPortal: async (id: string, portalDisabled: boolean, disabledReason?: string) => {
        const r = await apiPut<OneUser>(`/users/${id}/portal`, { portalDisabled, disabledReason })
        return r.user
    },
    listReviewers: async (): Promise<AppUser[]> => {
        const r = await apiGet<{ success: boolean; reviewers: AppUser[] }>('/users/reviewers')
        return r.reviewers ?? []
    },

    listPayments: async (): Promise<Payment[]> => {
        const r = await apiGet<ListPayments>('/payments')
        return r.payments ?? []
    },
    verifyPayment: async (id: string, status: 'completed' | 'rejected', notes?: string) => {
        const r = await apiPut<Success<{ payment: Payment }>>(`/payments/${id}/verify`, {
            status,
            notes
        })
        return r.payment
    },

    dashboardStats: async (): Promise<DashboardStats> => {
        const r = await apiGet<StatsResponse>('/users/stats')
        return (
            r.stats ?? {
                totalSubs: 0,
                underReview: 0,
                accepted: 0,
                published: 0,
                totalUsers: 0,
                pendingFee: 0,
                feeCollected: 0
            }
        )
    },
    activityLogs: async (): Promise<ActivityLogEntry[]> => {
        const r = await apiGet<{ success: boolean; logs: ActivityLogEntry[] }>('/users/logs')
        return r.logs ?? []
    },

    exportSubmissions: () => downloadBlob('/users/export/submissions', 'submissions.xlsx'),
    exportPayments: () => downloadBlob('/users/export/payments', 'payments.xlsx')
}

async function downloadBlob(path: string, filename: string): Promise<void> {
    const res = await api.get(path, { responseType: 'blob' })
    const url = URL.createObjectURL(new Blob([res.data]))
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
    URL.revokeObjectURL(url)
}


export const profileService = {
    update: async (patch: Partial<Pick<AppUser, 'name' | 'phone' | 'institution' | 'department' | 'specialization'>>) => {
        const r = await apiPut<OneUser>('/auth/profile', patch)
        return r.user
    },
    changePassword: (input: { currentPassword: string; newPassword: string }) =>
        apiPut<{ success: boolean; message: string }>('/auth/password', input)
}


export const notificationService = {
    list: async (): Promise<AppNotification[]> => {
        const r = await apiGet<ListNotifications>('/notifications')
        return r.notifications ?? []
    },
    markRead: (id: string) => apiPut<{ success: boolean }>(`/notifications/${id}/read`),
    markAllRead: () => apiPut<{ success: boolean }>('/notifications/read-all')
}


export type AnnouncementType = 'cfp' | 'event' | 'notice'

export interface SiteAnnouncement {
    id: string
    type: AnnouncementType
    title: string
    body?: string | null
    meta?: string | null
    linkUrl?: string | null
    linkLabel?: string | null
    image?: string | null
    active: boolean
    sortOrder: number
    createdAt?: string
    updatedAt?: string
}

export interface AnnouncementInput {
    type: AnnouncementType
    title: string
    body?: string
    meta?: string
    linkUrl?: string
    linkLabel?: string
    active?: boolean
    sortOrder?: number
    image?: File | null
    removeImage?: boolean
}

const announcementFormData = (input: Partial<AnnouncementInput>): FormData => {
    const fd = new FormData()
    if (input.type !== undefined) fd.append('type', input.type)
    if (input.title !== undefined) fd.append('title', input.title)
    if (input.body !== undefined) fd.append('body', input.body)
    if (input.meta !== undefined) fd.append('meta', input.meta)
    if (input.linkUrl !== undefined) fd.append('linkUrl', input.linkUrl)
    if (input.linkLabel !== undefined) fd.append('linkLabel', input.linkLabel)
    if (input.active !== undefined) fd.append('active', String(input.active))
    if (input.sortOrder !== undefined) fd.append('sortOrder', String(input.sortOrder))
    if (input.image) fd.append('image', input.image)
    if (input.removeImage) fd.append('removeImage', 'true')
    return fd
}

export const resolveUploadUrl = (p?: string | null): string | null => {
    if (!p) return null
    if (/^https?:\/\//i.test(p)) return p
    return `${env.UPLOADS_BASE_URL}/${p.replace(/^\/?uploads\//i, '')}`
}

export interface PublishedArticle {
    id: string
    journalId: string
    title: string
    abstract?: string | null
    keywords?: string | null
    authors: string
    publishedAt: string
    hasFile: boolean
}

export const siteService = {
    listAnnouncements: async (): Promise<SiteAnnouncement[]> => {
        const r = await apiGet<{ success: boolean; announcements: SiteAnnouncement[] }>('/site/announcements')
        return r.announcements ?? []
    },
    listPublished: async (): Promise<PublishedArticle[]> => {
        const r = await apiGet<{ success: boolean; articles: PublishedArticle[] }>('/site/published')
        return r.articles ?? []
    },
    getPublished: async (id: string): Promise<PublishedArticle> => {
        const r = await apiGet<{ success: boolean; article: PublishedArticle }>(`/site/published/${id}`)
        return r.article
    },
    publishedFileUrl: (id: string) => apiUrl(`/site/published/${id}/file`),
    listAllAnnouncements: async (): Promise<SiteAnnouncement[]> => {
        const r = await apiGet<{ success: boolean; announcements: SiteAnnouncement[] }>('/site/announcements/all')
        return r.announcements ?? []
    },
    createAnnouncement: async (input: AnnouncementInput): Promise<SiteAnnouncement> => {
        const r = await apiPostForm<{ success: boolean; announcement: SiteAnnouncement }>(
            '/site/announcements',
            announcementFormData(input)
        )
        return r.announcement
    },
    updateAnnouncement: async (id: string, input: Partial<AnnouncementInput>): Promise<SiteAnnouncement> => {
        const r = await apiPutForm<{ success: boolean; announcement: SiteAnnouncement }>(
            `/site/announcements/${id}`,
            announcementFormData(input)
        )
        return r.announcement
    },
    deleteAnnouncement: (id: string) => apiDelete<{ success: boolean }>(`/site/announcements/${id}`)
}


export const fileService = {
    downloadUrl: (submissionId: string, type: 'main' | 'revision' | 'reviewer_comment' = 'main') =>
        apiUrl(`/submissions/${submissionId}/download?type=${type}`),

    async download(
        submissionId: string,
        type: 'main' | 'revision' | 'reviewer_comment' = 'main',
        filename?: string
    ) {
        const r = await api.get(`/submissions/${submissionId}/download`, {
            params: { type },
            responseType: 'blob'
        })
        const blob = new Blob([r.data])
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename || `${submissionId}-${type}`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
    }
}
